import React, { memo, useCallback, useMemo } from 'react';
import { TabNotes, NotesTimeline } from '@modules/notes/TabNotes';
import { ContactActivityRequest } from '@business/entities/contact';
import { useObservable } from '@core/utils/hooks/rxjs';
import { DealResponse } from '@business/entities/deal';
import { DealOverlayBloc } from '../DealOverlayBloc';
import { Card, CardBody, CardTitle, Row } from 'reactstrap';
import { R } from '@core/utils/r';
import { TaskList } from '@modules/dashboard/DashboardPage/TasksMenu/TaskList';
import { SortByDirections, ChecklistId } from '@business/entities/checklist';
import { DealCalendarEvents } from '../DealCalendarEvents';
import { ProposalId } from '@business/entities/proposal';
import { DealProposals } from '../DealProposals';
import { switchMap, tap } from 'rxjs/operators';
import { DropdownSelector } from '@modules/common/DropdownSelector';
import { GCCalendarEventsList } from '@modules/common/GCCalendarEventsList';
import { GCCalendarEventObjectType } from '@business/entities/gc_calendar';
import { isTenants } from '@business/services/common_service/common_service';
import { CommonService } from '@business/services';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { FileResponse } from '@business/entities/file/FolderResponse';
import { opemFormViewModal } from '@modules/form/FormViewModal';
import { DealPresentations } from '../DealPresentations';
import { PermissionService } from '@business/services/permission_service';

export interface DealCommunicationTabProps {
  bloc: DealOverlayBloc;
  deal: DealResponse;
  scrollableDiv?: string | null;
}

export const DealCommunicationTab = memo((props: DealCommunicationTabProps) => {
  const { bloc, deal, scrollableDiv = null } = props;
  const taskStatuses = useObservable(bloc.taskStatuses$, []);
  const tasks = useObservable(bloc.tasks$, []);
  const events = useObservable(bloc.events$, []);
  const selectedProposalIds = useMemo(() => deal.proposals.map(p => p.id), [deal.proposals]);
  const [allActivity, setAllActivity] = React.useState(true);
  const onAdd = useCallback(
    (activity: ContactActivityRequest) => {
      return bloc.createActivity({ ...activity, objectType: 'deal', objectId: deal.id });
    },
    [bloc, deal.id],
  );

  const onAssign = useCallback((type: string, checklistId: ChecklistId, assignees: string[]) => {}, []);

  const onProposalAdd = useCallback(
    (proposalId: ProposalId) => {
      return bloc.addProposal(deal.id, proposalId).subscribe();
    },
    [bloc, deal.id],
  );

  const onProposalDelete = useCallback(
    (proposalId: ProposalId) => {
      return bloc.deleteProposal(deal.id, proposalId).subscribe();
    },
    [bloc, deal.id],
  );
  const salerep = useMemo(() => {
    if (isTenants(['1715030651'])) {
      const slRep = R.toNumber(R.get(deal.customFields, '5'));
      if (slRep > 0) {
        return slRep as any;
      }
    }
    return undefined;
  }, [deal]);

  const onFormSelect = useCallback((item: FileResponse) => {
    opemFormViewModal({ title: item.name, fileForm: item.form! });
  }, []);

  const settings = useSettings();
  const showInternalScheduler = useMemo(() => CommonService.isGCInternalSchedularEnabled(settings), [settings]);
  const restricted = PermissionService.useVipRestricted(deal.organization);

  return (
    <div>
      <TabNotes
        meta={{
          type: 'deal',
          id: deal.id,
          title: deal.name,
          description: `${window.location.origin}/deals/${deal.id}/${deal.orgId}`,
        }}
        onAdd={onAdd}
        contactId={deal.contact?.id}
        orgId={deal.orgId}
        defaultEmail={restricted || R.isEmpty(deal.contact ? deal.contact.email : '') ? undefined : deal.contact.email}
        availableEmails={restricted || !deal.contact ? [] : [{ label: deal.contact.name, value: deal.contact.email }]}
        onReload={() => {
          if (deal && deal.contact && !R.isEmpty(deal.contact.email)) {
            bloc.loadEvents(deal.contact.email);
            bloc.onReload();
          }
        }}
        linkProposal={true}
        onProposalAdd={onProposalAdd}
        salerep={salerep}
        selectedProposalIds={selectedProposalIds}
        onFormSelect={onFormSelect}
        hideAppointments={showInternalScheduler}
      />
      {showInternalScheduler && <GCCalendarEventsList objectId={deal.id} objectType={GCCalendarEventObjectType.Deal} orgId={deal.orgId} />}
      <Row>
        {events.length > 0 && (
          <DealCalendarEvents
            events={events}
            onDelete={id =>
              bloc
                .deleteEvent('0', id)
                .pipe(tap(() => bloc.loadEvents(deal.contact!.email)))
                .subscribe()
            }
          />
        )}

        {deal.proposals.length > 0 && <DealProposals proposals={deal.proposals} onDelete={onProposalDelete} />}
      </Row>
      <Row>
        <DealPresentations orgId={deal.orgId} />
      </Row>

      {tasks.length > 0 && (
        <TaskList
          bloc={bloc as any}
          isCompleted={false}
          noChecklists
          onAssign={onAssign}
          canCreateTasks={true}
          parentSectionId="custom-modal"
          sortByDirection={SortByDirections.asc}
          taskStatuses={taskStatuses}
          orgId={deal.orgId}
          objectId={deal.id}
          objectType="deal"
        />
      )}
      <Card>
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-5 ">
            <CardTitle>Timeline</CardTitle>
          </div>

          <NotesTimeline
            orgId={deal.organization.id}
            type="deal"
            objectId={deal.id}
            showAll={allActivity}
            loadContacts
            scrollableDiv={scrollableDiv}
            restricted={restricted}
          />
        </CardBody>
      </Card>
    </div>
  );
});
